import { Globe, RefreshCw, MoveRight, FileText } from "lucide-react";
import circleBadge from "@/assets/circle-badge.webp.asset.json";
import mockupReunion from "@/assets/mockup-reunion-laptop.webp.asset.json";
import mockupGroveland from "@/assets/mockup-groveland-macbook.webp.asset.json";
import mockupSongSavant from "@/assets/mockup-song-savant-ipad.webp.asset.json";
import mockupPhantom from "@/assets/mockup-phantom-iphone.webp.asset.json";
import mockupKensSewer from "@/assets/mockup-kens-sewer-macbook.webp.asset.json";
import mockupPharmacy from "@/assets/mockup-pharmacy-iphone.webp.asset.json";

const SCREEN_MOCKUPS = [
  { src: mockupReunion.url, alt: "Reunion event website on a laptop at a cafe table" },
  { src: mockupGroveland.url, alt: "Groveland leadership coaching website on a MacBook" },
  { src: mockupSongSavant.url, alt: "Song Savant music website on a tablet" },
  { src: mockupPhantom.url, alt: "Phantom Concrete Solutions website on an iPhone" },
  { src: mockupKensSewer.url, alt: "Ken's Sewer Service website on a MacBook" },
  { src: mockupPharmacy.url, alt: "Pharmacy CBS website on an iPhone" },
];



const SERVICES = [
  {
    icon: Globe,
    title: "New Websites",
    description: "Build a modern Squarespace presence from the ground up.",
  },
  {
    icon: RefreshCw,
    title: "Website Refreshes",
    description: "Give an existing Squarespace site a stronger design, structure and user experience.",
  },
  {
    icon: MoveRight,
    title: "CMS Migrations",
    description: "Move from WordPress or another CMS to a simpler, easier-to-manage Squarespace experience.",
  },
  {
    icon: FileText,
    title: "Landing Pages",
    description: "Launch focused pages for campaigns, events, offers and lead generation without rebuilding your entire website.",
  },
];

interface SquarespaceSectionProps {
  eyebrow?: string;
  headline?: React.ReactNode;
  lead?: string;
}

export function SquarespaceSection({
  eyebrow = "Also Available",
  headline = (
    <>
      Squarespace Websites &amp; <span className="text-primary">Landing Pages</span>
    </>
  ),
  lead = "Build a better digital home for your business, modern Squarespace websites and landing pages that are clean, credible, easy to manage and built around your marketing goals.",
}: SquarespaceSectionProps = {}) {
  return (
    <section id="squarespace" className="border-t border-border/50 bg-[#080E1E]/95 px-5 py-12 sm:px-6 sm:py-16 md:px-8 md:py-20 lg:px-12">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8 grid gap-6 sm:mb-10 sm:gap-8 md:mb-14 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20 lg:items-start">
          <div>
            <p className="text-xs font-semibold tracking-[0.22em] uppercase text-primary mb-4">
              {eyebrow}
            </p>
            <h2 className="text-3xl sm:text-4xl font-semibold leading-tight">{headline}</h2>
          </div>
          <div className="lg:pt-11">
            <p className="text-muted-foreground leading-relaxed lg:text-lg">{lead}</p>
            <div className="mt-7 flex items-center gap-3">
              <img
                src={circleBadge.url}
                alt="Squarespace Circle Member badge"
                className="h-11 w-auto opacity-90 hover:opacity-100 transition-opacity"
                loading="lazy"
              />
              <span className="text-sm text-muted-foreground">Official Squarespace Circle Member</span>
            </div>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-2 gap-3 sm:mt-12 sm:grid-cols-3 sm:gap-4 md:mt-14 md:gap-6">
          {SCREEN_MOCKUPS.map((mockup) => (
            <figure key={mockup.src} className="aspect-[4/3]">
              <div className="h-full w-full overflow-hidden rounded-lg border border-white/10 bg-black shadow-[var(--shadow-glow)] transition-transform duration-500 hover:scale-[1.015] sm:rounded-2xl">
                <img src={mockup.src} alt={mockup.alt} loading="lazy" className="w-full h-full object-cover" />
              </div>
            </figure>
          ))}
        </div>

        <div className="mt-8 grid gap-4 sm:mt-12 sm:grid-cols-2 sm:gap-6 md:mt-14 lg:grid-cols-4 lg:gap-8">
          {SERVICES.map((service) => {
            const Icon = service.icon;
            return (
              <div key={service.title} className="rounded-lg border border-border/50 bg-card/30 p-5 sm:rounded-xl sm:p-6">
                <Icon className="w-6 h-6 text-primary mb-4" />
                <h3 className="text-lg font-semibold mb-2">{service.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{service.description}</p>
              </div>
            );
          })}
        </div>

        <p className="mt-10 md:mt-12 border-l-2 border-primary pl-5 text-muted-foreground leading-relaxed max-w-3xl">

          And your website doesn’t need to be on Squarespace to use one of our lead generation tools. Calculators, assessments and custom apps can integrate with virtually any website or launch as standalone experiences.
        </p>
      </div>
    </section>
  );
}
